/** AbortSignal composition.
 *
 *  Every fetch helper in the app has to honour TWO cancellation sources at
 *  once: the caller's signal (navigation, unmount, a newer request) and its own
 *  timeout. Overwriting the caller's signal with a private one is how the 5 s
 *  and 60 s action timeouts in the mail view silently stopped firing, so this
 *  is the one place that composes them.
 *
 *  Pure (no DOM beyond AbortController/AbortSignal, which Node also has), so it
 *  is unit-tested under `node --test`. */

type SignalLike = AbortSignal | null | undefined;

/** A signal that aborts as soon as any input aborts, carrying that input's
 *  `reason`. Uses the native `AbortSignal.any` where present and falls back to
 *  manual wiring for older engines. */
export function anySignal(signals: SignalLike[]): AbortSignal {
  const list = signals.filter((s): s is AbortSignal => !!s);
  if (list.length === 0) return new AbortController().signal;
  if (list.length === 1) return list[0];
  const native = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof native === "function") return native.call(AbortSignal, list);
  return anySignalManual(list);
}

/** The fallback path of {@link anySignal}, exported so it can be tested on an
 *  engine that has the native API. */
export function anySignalManual(list: AbortSignal[]): AbortSignal {
  const ctrl = new AbortController();
  const already = list.find((s) => s.aborted);
  if (already) {
    ctrl.abort(already.reason);
    return ctrl.signal;
  }
  const listeners: Array<[AbortSignal, () => void]> = [];
  const cleanup = () => {
    for (const [s, fn] of listeners) s.removeEventListener("abort", fn);
    listeners.length = 0;
  };
  for (const s of list) {
    const fn = () => {
      if (!ctrl.signal.aborted) ctrl.abort(s.reason);
      cleanup();
    };
    listeners.push([s, fn]);
    s.addEventListener("abort", fn, { once: true });
  }
  return ctrl.signal;
}
